// src/components/common/ChatInterface.tsx
import React, { useState, useEffect, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { 
  fetchThreadMessages, 
  sendMessage, 
  markThreadAsRead
} from '../../store/slices/chatSlice';
import { realtimeService } from '../../services/realtimeService';

interface ChatInterfaceProps {
  threadId: string | null;
  appointmentId?: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ threadId, appointmentId }) => {
  const dispatch = useAppDispatch();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Get data from Redux store
  const { user } = useAppSelector(state => state.auth);
  const { messages, currentThread, isLoading } = useAppSelector(state => state.chat);
  
  // Component state
  const [messageInput, setMessageInput] = useState('');
  
  // Fetch messages when component mounts or threadId changes
  useEffect(() => {
    if (threadId) {
      dispatch(fetchThreadMessages(threadId));
      dispatch(markThreadAsRead(threadId));
    }
  }, [dispatch, threadId]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Handle sending a message
  const handleSendMessage = () => {
    if (!messageInput.trim() || !user || !threadId) return;
    
    dispatch(sendMessage({
      threadId,
      content: messageInput,
      senderId: user.id,
      senderName: user.name,
      senderRole: user.role,
      senderPhoto: user.profilePhoto?.url
    }));
    
    // Also send through realtime service if available
    if (realtimeService.isConnected()) {
      realtimeService.sendChatMessage(threadId, messageInput)
        .catch(error => console.error('Error sending message:', error));
    }
    
    setMessageInput('');
  };
  
  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setMessageInput(e.target.value);
  };
  
  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  // Format timestamp
  const formatTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return timestamp;
    }
  };
  
  // Get chat partner info
  const getChatPartner = () => {
    if (!currentThread || !user) return null;
    
    const isClient = user.role === 'client';
    return {
      name: isClient ? currentThread.stylistName : currentThread.clientName,
      photo: isClient ? currentThread.stylistPhoto : currentThread.clientPhoto
    };
  };
  
  const chatPartner = getChatPartner();
  
  return (
    <div className="chat-interface">
      {/* Chat Header */}
      <div className="chat-interface__header">
        {chatPartner ? (
          <div className="chat-interface__partner">
            <div className="chat-interface__avatar">
              {chatPartner.photo ? (
                <img 
                  src={chatPartner.photo} 
                  alt={chatPartner.name}
                />
              ) : (
                <div className="chat-interface__avatar-placeholder">
                  {chatPartner.name.charAt(0)}
                </div>
              )}
            </div>
            <div className="chat-interface__partner-info">
              <h3 className="chat-interface__partner-name">{chatPartner.name}</h3>
              {appointmentId && (
                <p className="chat-interface__appointment-id">
                  This appointment
                </p>
              )}
            </div>
          </div>
        ) : (
          <h3 className="chat-interface__title">Chat</h3>
        )}
      </div>
      
      <div className="chat-interface__divider"></div>
      
      {/* Messages Area */}
      <div className="chat-interface__messages">
        {isLoading && messages.length === 0 ? (
          <div className="chat-interface__loading">
            <div className="spinner"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="chat-interface__empty">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message, index) => {
            const isCurrentUser = user?.id === message.senderId;
            const showAvatar = index === 0 || messages[index - 1].senderId !== message.senderId;
            
            return (
              <div
                key={message.id}
                className={`chat-message ${isCurrentUser ? 'chat-message--outgoing' : 'chat-message--incoming'}`}
              >
                {!isCurrentUser && showAvatar && (
                  <div className="chat-message__avatar">
                    {message.senderPhoto ? (
                      <img 
                        src={message.senderPhoto} 
                        alt={message.senderName} 
                      />
                    ) : (
                      <div className="chat-message__avatar-placeholder">
                        {message.senderName.charAt(0)}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="chat-message__content">
                  {!isCurrentUser && showAvatar && (
                    <p className="chat-message__sender">
                      {message.senderName}
                    </p>
                  )}
                  
                  <div className="chat-message__bubble">
                    <p className="chat-message__text">
                      {message.content}
                    </p>
                    <span className="chat-message__time">
                      {formatTime(message.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Message Input */}
      <div className="chat-interface__input-area">
        <div className="chat-interface__input-container">
          <textarea
            className="chat-interface__input"
            placeholder="Type a message..."
            value={messageInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            rows={1}
          />
          
          <button 
            className={`chat-interface__send-button ${!messageInput.trim() ? 'chat-interface__send-button--disabled' : ''}`}
            onClick={handleSendMessage}
            disabled={!messageInput.trim()}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;